import { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useUserProfile } from '@/hooks/useUserProfile';
import { checkExistingReferral, saveReferral } from '@/services/quotes/referrals';
import { supabase } from '@/integrations/supabase/client';
import i18n from '@/i18n/config';

interface UsePhoneRevealProps {
  professionalName: string;
  professionalId: string;
  profession: string;
  onBeforeReveal?: () => boolean;
  autoReveal: boolean;
  persistRevealState?: boolean; // Whether to check existing referrals and keep revealed state
}

export const usePhoneReveal = ({
  professionalName,
  professionalId,
  profession,
  onBeforeReveal,
  autoReveal,
  persistRevealState = false
}: UsePhoneRevealProps) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const [revealedPhoneNumber, setRevealedPhoneNumber] = useState<string>('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showPhoneRequiredDialog, setShowPhoneRequiredDialog] = useState(false);
  const [showProfileIncompleteDialog, setShowProfileIncompleteDialog] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user, checkPhoneVerification } = useAuth();
  const { profile } = useUserProfile();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Check if user already revealed this professional's phone before - only if persistRevealState is true
  useEffect(() => {
    const checkPreviousReveal = async () => {
      if (persistRevealState && user && professionalId) {
        const { exists, phoneNumber } = await checkExistingReferral(user.id, professionalId);
        if (exists && phoneNumber) {
          setRevealedPhoneNumber(phoneNumber);
          setIsRevealed(true);
        }
      }
    };
    
    checkPreviousReveal();
  }, [persistRevealState, user, professionalId]);

  // Auto-reveal the phone number if autoReveal is true and user is logged in with complete profile
  useEffect(() => {
    const checkAutoReveal = async () => {
      if (autoReveal && user && profile) {
        const hasCompleteProfile = profile.name && profile.phone;
        if (hasCompleteProfile) {
          await revealPhoneNumber();
        }
      }
    };
    
    checkAutoReveal();
  }, [autoReveal, user, profile, professionalId, professionalName, profession]);

  const handleReveal = async () => {
    if (onBeforeReveal && !onBeforeReveal()) {
      return;
    }
    
    if (!user) {
      setIsDialogOpen(true);
      return;
    }
    
    // Check if user has complete profile (name and phone)
    if (!profile || !profile.name || !profile.phone) {
      setShowProfileIncompleteDialog(true);
      return;
    }
    
    // Show consent dialog before revealing
    setShowConsentDialog(true);
  };

  // Function to reveal phone number securely
  const revealPhoneNumber = async () => {
    if (!user || !profile) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_professional_phone_secure', {
        professional_id_param: professionalId,
        user_name_param: profile.name
      });

      if (error) {
        console.error('Error revealing phone:', error);
        toast({
          title: i18n.t('phoneReveal.error'),
          description: i18n.t('phoneReveal.errorRevealingNumber'),
          variant: "destructive"
        });
        return;
      }

      if (data && data.length > 0) {
        const result = data[0];
        if (result.success) {
          setRevealedPhoneNumber(result.phone_number);
          setIsRevealed(true);
          
          // Save referral for tracking
          try {
            await saveReferral(
              user.id,
              professionalId,
              professionalName,
              result.phone_number,
              profession,
              profile.name
            );
          } catch (error) {
            console.error("Error saving referral:", error);
          }
          
          toast({
            title: i18n.t('phoneReveal.numberRevealedSuccess'),
            description: i18n.t('phoneReveal.canContactProfessional'),
          });
        } else {
          toast({
            title: i18n.t('phoneReveal.cannotRevealNumber'),
            description: result.message,
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      console.error('Error revealing phone:', error);
      toast({
        title: i18n.t('phoneReveal.error'),
        description: i18n.t('phoneReveal.errorRevealingNumber'),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConsentApproved = async () => {
    setShowConsentDialog(false);
    await revealPhoneNumber();
  };

  const handleGoToLogin = () => {
    setIsDialogOpen(false);
    navigate('/login', { 
      state: { returnUrl: window.location.pathname }
    });
  };

  const handleGoToProfile = () => {
    setShowProfileIncompleteDialog(false);
    navigate('/settings');
  };

  const handleVerificationComplete = async () => {
    setShowPhoneRequiredDialog(false);
    await revealPhoneNumber();

    toast({
      title: i18n.t('phoneReveal.verificationCompleted'),
      description: i18n.t('phoneReveal.canViewPhoneNumbers'),
    });
  };

  return {
    isRevealed,
    revealedPhoneNumber,
    isLoading,
    isDialogOpen,
    showPhoneRequiredDialog,
    showProfileIncompleteDialog,
    showConsentDialog,
    setIsDialogOpen,
    setShowPhoneRequiredDialog,
    setShowProfileIncompleteDialog,
    setShowConsentDialog,
    handleReveal,
    handleGoToLogin,
    handleGoToProfile,
    handleConsentApproved,
    handleVerificationComplete,
    user,
    profile
  };
};